import { useEffect, useState } from 'react'
import { Button } from '@/components/Button'
import { Card, CardBody, CardHeader } from '@/components/Card'
import { ErrorNotice } from '@/components/ErrorNotice'
import { Toggle } from '@/components/Form'
import { Skeleton } from '@/components/Skeleton'
import {
  useNotificationPreferences,
  useSaveNotificationPreferences,
} from '@/hooks/useNotifications'
import type { PreferenceMap } from '@/hooks/useNotifications'
import { eventLabel } from '@/lib/format'
import { NOTIFICATION_EVENTS } from '@/lib/types'

export function NotificationsCard() {
  const preferences = useNotificationPreferences()
  const save = useSaveNotificationPreferences()
  const [draft, setDraft] = useState<PreferenceMap | null>(null)

  const server = preferences.data ?? null
  useEffect(() => {
    if (server) setDraft(server)
  }, [server])

  const dirty =
    draft !== null && server !== null && NOTIFICATION_EVENTS.some((e) => draft[e] !== server[e])

  return (
    <Card>
      <CardHeader
        title="What to notify me about"
        description="Applies to push notifications and the list on your dashboard."
        action={
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => draft && save.mutate(draft)}
          >
            Save
          </Button>
        }
      />
      {/* Full-bleed so each preference is a board row rather than a boxed
          setting — the hairlines line up with every other list in the app. */}
      <CardBody flush>
        {preferences.isPending ? (
          <div className="space-y-2 px-4 py-4 sm:px-5">
            {NOTIFICATION_EVENTS.map((event) => (
              <Skeleton key={event} className="h-14 w-full" />
            ))}
          </div>
        ) : preferences.isError ? (
          <div className="px-4 py-4 sm:px-5">
            <ErrorNotice error={preferences.error} onRetry={() => void preferences.refetch()} />
          </div>
        ) : draft ? (
          <div>
            {NOTIFICATION_EVENTS.map((event) => {
              const meta = eventLabel(event)
              return (
                <div key={event} className="board-row px-4 sm:px-5">
                  <Toggle
                    label={meta.title}
                    description={meta.description}
                    checked={draft[event]}
                    onChange={(next) => setDraft({ ...draft, [event]: next })}
                  />
                </div>
              )
            })}
          </div>
        ) : null}
      </CardBody>
    </Card>
  )
}
